// GameClock.jsx
import React, { useState, useEffect, useRef } from "react";
import {
    Paper,
    Button,
    Typography,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
} from "@material-ui/core";
import { Add, Remove } from "@material-ui/icons";
import { saveEvent } from "./gameStorage";
import { useSelector } from "react-redux";
import SettingsIcon from "@material-ui/icons/Settings";
import ShotClock24 from "./ShotClock24";

const formatTime = (seconds) => {
    const safe = Math.max(0, Number(seconds) || 0);
    const m = Math.floor(safe / 60).toString().padStart(2, "0");
    const s = Math.floor(safe % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
};

const MAX_SHOT_TIME = 24;

const GameClock = ({
    onPeriodTimeChange,
    onPeriodEnd,
    onStart,
    onPause,
    initialElapsedTime = 0,
    currentPeriodProp,
    clock, // { tiempoSegundos, runningClock, timestampClock }
    callbackElapsedTime
}) => {
    const [currentPeriod, setCurrentPeriod] = useState(currentPeriodProp || "1");
    const [elapsedTime, setElapsedTime] = useState(0); // remaining del periodo
    const [running, setRunning] = useState(false);

    //callbackElapsedTime(elapsedTime)
    // Shot clock
    const [shotRemaining, setShotRemaining] = useState(MAX_SHOT_TIME);
    const [shotRunning, setShotRunning] = useState(false);

    const [settingsOpen, setSettingsOpen] = useState(false);
    const [periodMinutes, setPeriodMinutes] = useState(10);
    const [totalPeriods, setTotalPeriods] = useState(4);

    const intervalRef = useRef(null);

    // ✅ Solo hidratar desde clock una vez (en recarga)
    const didHydrateFromClockRef = useRef(false);

    const periodDuration = periodMinutes * 60;

    // Inicializar por default SOLO si no hidratamos desde clock
    useEffect(() => {
        if (didHydrateFromClockRef.current) return;
        const initial = Math.max(0, periodDuration - initialElapsedTime);
        setElapsedTime(initial);
    }, [initialElapsedTime, periodDuration]);

    useEffect(() => {
        if (currentPeriodProp) setCurrentPeriod(currentPeriodProp);
    }, [currentPeriodProp]);

    // ✅ Hidratación inicial desde clock (espera a que llegue async)
    useEffect(() => {
        if (didHydrateFromClockRef.current) return;
        if (!clock) return;

        const tiempo = Number(clock.tiempoSegundos);
        const ts = Number(clock.timestampClock);
        const run = !!clock.runningClock;

        // Ignora placeholder tipo {0, false, 0}
        const isUsable = Number.isFinite(tiempo) && Number.isFinite(ts) && ts > 0;
        if (!isUsable) return;

        let corrected = Math.max(0, tiempo);

        // ✅ si estaba corriendo, descuenta el tiempo transcurrido desde timestampClock
        if (run) {
            const elapsedSince = Math.floor((Date.now() - ts) / 1000);
            corrected = Math.max(0, corrected - elapsedSince);
        }

        setElapsedTime(corrected);
        setRunning(run);

        // (Shot clock no se hidrata aquí)
        didHydrateFromClockRef.current = true;
    }, [clock]);

    // Loop del reloj general + shot clock
    useEffect(() => {
        if (running) {
            intervalRef.current = setInterval(() => {
                // Reloj general
                setElapsedTime((prev) => {
                    const newTime = prev - 1;

                    if (onPeriodTimeChange) onPeriodTimeChange(currentPeriod, newTime);

                    if (newTime <= 0) {
                        clearInterval(intervalRef.current);
                        intervalRef.current = null;

                        setRunning(false);
                        setShotRunning(false);

                        if (onPeriodEnd) onPeriodEnd(currentPeriod);

                        saveEvent({
                            tipo: "cambio_periodo",
                            cuarto: currentPeriod,
                            tiempoSegundos: 0,
                        });

                        return 0;
                    }

                    return newTime;
                });

                // Shot clock
                setShotRemaining((prev) => {
                    if (!shotRunning) return prev;
                    if (prev <= 1) {
                        console.log("Violación de 24s");
                        setShotRunning(false);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        } else if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [running, shotRunning, currentPeriod, onPeriodEnd, onPeriodTimeChange]);

    // START / PAUSE del reloj general
    const handleStart = () => {
        if (elapsedTime <= 0) return;
        setRunning(true);
        if (onStart) onStart(elapsedTime);
    };

    const handlePause = () => {
        setRunning(false);
        if (onPause) onPause(elapsedTime);
    };

    const handleReset = () => {
        setRunning(false);
        setShotRunning(false);
        setElapsedTime(periodDuration);
        setShotRemaining(MAX_SHOT_TIME);
        if (onPeriodTimeChange) onPeriodTimeChange(currentPeriod, periodDuration);
    };

    const handlePeriodChange = (e) => {
        const newPeriod = e.target.value;

        setRunning(false);
        setShotRunning(false);
        setCurrentPeriod(newPeriod);

        setElapsedTime(periodDuration);
        setShotRemaining(MAX_SHOT_TIME);

        if (onPeriodTimeChange) onPeriodTimeChange(newPeriod, periodDuration);

        saveEvent({
            tipo: "cambio_periodo",
            cuarto: newPeriod,
            tiempoSegundos: periodDuration,
        });
    };

    const adjustTime = (delta) => {
        setElapsedTime((prev) => {
            const newTime = Math.max(0, prev + delta);
            if (onPeriodTimeChange) onPeriodTimeChange(currentPeriod, newTime);
            return newTime;
        });
    };

    // Callbacks ShotClock
    const handleShotPlay = () => {
        if (shotRemaining === 0) return;
        if (!running && elapsedTime > 0) {
            handleStart(elapsedTime);
        }
        setShotRunning(true);
    };

    const handleShotPause = () => {
        setShotRunning(false);
        if (running) {
            handlePause(elapsedTime);
        }
    };

    const handleShotReset = () => setShotRemaining(MAX_SHOT_TIME);
    const handleShotAdd = () =>
        setShotRemaining((prev) => Math.min(MAX_SHOT_TIME, prev + 1));
    const handleShotRemove = () =>
        setShotRemaining((prev) => Math.max(0, prev - 1));

    return (
        <Paper
            style={{
                padding: 10,
                position: "fixed",
                bottom: 0,
                left: 0,
                width: "100%",
                textAlign: "center",
                zIndex: 1000,
                backgroundColor: "#e0e0e0",
            }}
        >
            <Grid container spacing={2} alignItems="center">
                {/* IZQUIERDA: settings */}
                <Grid item xs={1} sm={1}>
                    <IconButton
                        variant="text"
                        style={{ fontSize: 40, padding: 15 }}
                        onClick={() => setSettingsOpen(true)}
                    >
                        <SettingsIcon />
                    </IconButton>
                </Grid>

                {/* CENTRO: periodo + tiempo + +/- */}
                <Grid item xs={5} sm={5}>
                    <Grid container direction="column" alignItems="center">
                        <Grid item>
                            <Typography variant="h5" align="center">
                                Periodo: {currentPeriod}
                            </Typography>
                        </Grid>

                        <Grid item>
                            <Typography variant="h3" align="center" style={{ fontFamily: "Digital" }} >
                                {formatTime(elapsedTime)}
                            </Typography>
                        </Grid>

                        <Grid item style={{ marginTop: 2 }}>
                            <IconButton onClick={() => adjustTime(-5)}>
                                <Remove />
                            </IconButton>
                            <IconButton onClick={() => adjustTime(5)}>
                                <Add />
                            </IconButton>
                        </Grid>
                    </Grid>
                </Grid>

                {/* DERECHA: Shot clock */}
                <Grid item xs={6} sm={6}>
                    <ShotClock24
                        remaining={shotRemaining}
                        running={shotRunning}
                        onPlay={handleShotPlay}
                        onPause={handleShotPause}
                        onReset={handleShotReset}
                        onAdd={handleShotAdd}
                        onRemove={handleShotRemove}
                    />
                </Grid>
            </Grid>

            {/* Dialog config */}
            <Dialog
                open={settingsOpen}
                onClose={() => setSettingsOpen(false)}
                style={{ backgroundColor: "#e0e0e0" }}
            >
                <DialogTitle>Configuración del Reloj y Periodos</DialogTitle>

                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4} style={{ padding: 16 }}>
                        <FormControl fullWidth>
                            <InputLabel>Periodo</InputLabel>
                            <Select value={currentPeriod} onChange={handlePeriodChange}>
                                {Array.from({ length: totalPeriods }, (_, i) => (
                                    <MenuItem key={i + 1} value={(i + 1).toString()}>
                                        {`${i + 1}° Cuarto`}
                                    </MenuItem>
                                ))}
                                <MenuItem value="OT">Tiempo Extra</MenuItem>
                            </Select>
                        </FormControl>

                        <Button variant="outlined" onClick={handleReset} style={{ margin: 4 }}>
                            Reiniciar reloj
                        </Button>

                        <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
                            <Button
                                onClick={handleStart}
                                variant="contained"
                                color="primary"
                                disabled={elapsedTime <= 0 || running}
                                style={{ flex: 1 }}
                            >
                                Iniciar
                            </Button>
                            <Button
                                onClick={handlePause}
                                variant="contained"
                                color="secondary"
                                disabled={!running}
                                style={{ flex: 1 }}
                            >
                                Pausar
                            </Button>
                        </div>
                    </Grid>

                    <Grid item xs={12} sm={12}>
                        <DialogContent>
                            <TextField
                                label="Duración por cuarto (minutos)"
                                type="number"
                                fullWidth
                                value={periodMinutes}
                                onChange={(e) =>
                                    setPeriodMinutes(parseInt(e.target.value, 10) || 0)
                                }
                                margin="dense"
                            />
                            <TextField
                                label="Cantidad de cuartos"
                                type="number"
                                fullWidth
                                value={totalPeriods}
                                onChange={(e) => setTotalPeriods(parseInt(e.target.value, 10) || 0)}
                                margin="dense"
                            />
                        </DialogContent>
                    </Grid>

                    <DialogActions>
                        <Button onClick={() => setSettingsOpen(false)} color="primary">
                            Cerrar
                        </Button>
                    </DialogActions>
                </Grid>
            </Dialog>
        </Paper >
    );
};

export default GameClock;
